import type { ReactElement } from 'react';
import { Building2, Fingerprint, Blocks, Database, Bell, Palette, Tags, Server, Users, ShieldCheck, KeySquare, BookUser, CreditCard, type LucideIcon } from 'lucide-react';
import type { Permission } from '@bmai/shared';
import { useAuth } from '@/features/auth/AuthContext';
import FeatureNavLayout from '@/shared/surfaces/FeatureNavLayout';
import type { FeatureNavItem } from '@/shared/surfaces/FeatureNavTypes';

interface SettingsNavItem {
    path: string;
    label: string;
    icon: LucideIcon;
    permission?: Permission;
    role?: string;
}

/**
 * Ordered by traffic then clustered (integrations, governance, access,
 * preferences), each cluster kept adjacent.
 */
const SETTINGS_NAV_ITEMS: SettingsNavItem[] = [
    { path: '/settings/organization', label: 'Organization', icon: Building2, permission: { action: 'view', resource: 'organization' } },
    { path: '/settings/users', label: 'Users', icon: Users, permission: { action: 'view', resource: 'users' } },
    { path: '/settings/providers', label: 'AI Providers', icon: Blocks, permission: { action: 'view', resource: 'connections' } },
    { path: '/settings/identity-providers', label: 'Identity Providers', icon: Fingerprint, permission: { action: 'view', resource: 'identity' } },
    { path: '/settings/directory', label: 'Directory', icon: BookUser, permission: { action: 'view', resource: 'users' } },
    { path: '/settings/usage-categories', label: 'Usage Categories', icon: Tags, permission: { action: 'view', resource: 'usage_categories' } },
    { path: '/settings/billing', label: 'Billing', icon: CreditCard, permission: { action: 'view', resource: 'billing' } },
    { path: '/settings/data-retention', label: 'Data & Retention', icon: Database, permission: { action: 'view', resource: 'data_retention' } },
    { path: '/settings/roles', label: 'Roles & Permissions', icon: ShieldCheck, permission: { action: 'view', resource: 'roles' } },
    { path: '/settings/sso', label: 'Single Sign-On', icon: KeySquare, permission: { action: 'view', resource: 'identity' } },
    { path: '/settings/notifications', label: 'Notifications', icon: Bell, permission: { action: 'view', resource: 'notifications' } },
    { path: '/settings/theme', label: 'Theme', icon: Palette },
    { path: '/settings/system', label: 'System', icon: Server, permission: { action: 'view', resource: 'system' } },
];

/**
 * The Settings shell: the settings sections as a full-height interior sub-nav
 * column beside their scrollable content. Items the signed-in user lacks the
 * role/permission for are hidden.
 */
export default function SettingsLayout(): ReactElement {
    const { hasPermission, hasRole } = useAuth();

    const items: FeatureNavItem[] = SETTINGS_NAV_ITEMS.filter((item) => {
        if (item.role && !hasRole(item.role)) return false;
        if (item.permission && !hasPermission(item.permission)) return false;
        return true;
    }).map((item) => ({ path: item.path, label: item.label, icon: item.icon }));

    return <FeatureNavLayout title="Settings" ariaLabel="Settings section" groups={[{ items, selectGroupLabel: 'Settings' }]} />;
}
